// Acomptes d'ouverture (14/10/2026) : acomptes reçus avant le début de
// l'exercice et pas encore déduits. Modèle Excel → aperçu (rien n'est créé)
// → création en à-nouveau PROVISOIRE (890000 / 419100, sans TVA collectée),
// contre-passé à l'import du FEC. Les factures de solde qui les citent les
// déduisent ensuite à leur validation.
import { useEffect, useState } from "react";
import api from "../services/api";

const toBase64 = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result).split(",")[1] || "");
  r.onerror = reject;
  r.readAsDataURL(file);
});
const eur = (n) => `${Number(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const frDate = (d) => (d ? String(d).slice(0, 10).split("-").reverse().join("/") : "—");

export default function OpeningDepositsModal({ companyId, onClose }) {
  const [payload, setPayload] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // B7 (03/11/2026) : acomptes lus sur les PDF « AC… » (sans IA), TVA déjà déclarée confirmée ligne par ligne
  const [pdfRows, setPdfRows] = useState(null);
  const [pdfErrors, setPdfErrors] = useState([]);

  const loadLedger = () => api.openingDeposits.list(companyId).then(setLedger).catch(() => setLedger([]));
  useEffect(() => { loadLedger(); }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const choose = async (file) => {
    if (!file) return;
    setBusy(true); setError(""); setPreview(null); setResult(null);
    try {
      const data = { filename: file.name, content_base64: await toBase64(file) };
      setPayload(data);
      setPreview(await api.openingDeposits.preview(companyId, data));
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };
  const choosePdfs = async (files) => {
    if (!files?.length) return;
    setBusy(true); setError(""); setPreview(null); setResult(null); setPdfErrors([]);
    const rows = [...(pdfRows || [])], errs = [];
    for (const file of files) {
      try { const { row } = await api.openingDeposits.readPdf(companyId, file); if (!rows.some(r => r.ref === row.ref)) rows.push(row); }
      catch (e) { errs.push(`${file.name} : ${e.message}`); }
    }
    setPdfRows(rows); setPdfErrors(errs);
    if (rows.length) await previewRows(rows); else setBusy(false);
  };
  const previewRows = async (rows) => {
    setBusy(true); setError("");
    try { const data = { rows }; setPayload(data); setPreview(await api.openingDeposits.preview(companyId, data)); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };
  const setDeclared = (ref, value) => {
    const rows = pdfRows.map(r => (r.ref === ref ? { ...r, declared: value } : r));
    setPdfRows(rows); previewRows(rows);
  };
  const create = async () => {
    setBusy(true); setError("");
    try { setResult(await api.openingDeposits.create(companyId, payload)); setPreview(null); setPdfRows(null); loadLedger(); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };
  const template = async () => {
    try {
      const { blob, filename } = await api.openingDeposits.template();
      const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = filename; a.click(); setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) { setError(e.message); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 900, width: "95%" }}>
        <div className="modal-header"><h3 className="modal-title">📂 Acomptes d'ouverture</h3><button className="modal-close" onClick={onClose}>✕</button></div>
        <div style={{ fontSize: 13, marginBottom: 10 }}>
          Acomptes reçus <strong>avant le début de l'exercice</strong> et pas encore déduits sur une facture de solde. Ils sont repris au compte 419 au premier jour de l'exercice (à-nouveau provisoire, confirmé par le FEC de votre expert-comptable), sans nouvelle TVA. Une facture de solde qui cite l'acompte (AC…) le déduit ensuite à sa validation, et ne collecte que la TVA qui n'a pas encore été déclarée.
        </div>
        <div style={{ background: "#f8fafc", border: "1px solid #e5e7eb", borderRadius: 10, padding: "10px 14px", fontSize: 12.5, marginBottom: 12 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Format attendu</div>
          <div>Fichier Excel (.xlsx) — le plus simple : <button className="btn sm" onClick={template}>⬇ Télécharger le modèle Excel</button> — ou CSV « ; ».</div>
          <div style={{ marginTop: 4 }}>Colonnes obligatoires : <strong>Réf. acompte</strong> · <strong>Date</strong> · <strong>HT</strong> · <strong>TVA</strong> · <strong>TTC</strong> · <strong>TVA déjà déclarée</strong> (montant, ou oui / non). Facultatives : Client · Reste à déduire TTC (par défaut : tout le TTC).</div>
          <div style={{ marginTop: 4 }}>Dates JJ/MM/AAAA ; montants 1 234,56. Exemple : <code>AC2608-0123 ; 20/08/2026 ; Client Exemple ; 1 000,00 ; 200,00 ; 1 200,00 ; 200,00</code></div>
          <div style={{ marginTop: 4, color: "var(--text-muted)" }}>« TVA déjà déclarée » : à vérifier avec votre expert-comptable (TVA de l'acompte figurant sur une déclaration de l'exercice précédent).</div>
        </div>
        {error && <div style={{ background: "#e24b4a22", color: "#e24b4a", padding: "8px 12px", borderRadius: 8, marginBottom: 10, fontSize: 13, whiteSpace: "pre-wrap" }}>{error}</div>}
        <label className="btn primary" style={{ cursor: busy ? "wait" : "pointer" }}>
          Choisir le fichier des acomptes
          <input type="file" accept=".xlsx,.xls,.csv" style={{ display: "none" }} disabled={busy} onChange={e => { setPdfRows(null); choose(e.target.files[0]); e.target.value = ""; }} />
        </label>
        {" "}
        <label className="btn" style={{ cursor: busy ? "wait" : "pointer" }} title="PDF des factures d'acompte (AC…) exportés de Dolibarr, lus sans IA">
          Choisir les PDF des acomptes (AC…)
          <input type="file" accept="application/pdf" multiple style={{ display: "none" }} disabled={busy} onChange={e => { choosePdfs([...e.target.files]); e.target.value = ""; }} />
        </label>
        {pdfRows && pdfRows.length > 0 && <button className="btn" style={{ marginLeft: 6 }} disabled={busy} onClick={() => { setPdfRows(null); setPreview(null); setPdfErrors([]); }}>Vider la liste ({pdfRows.length} PDF)</button>}
        {pdfErrors.length > 0 && <div style={{ fontSize: 12, color: "#b91c1c", marginTop: 6 }}>{pdfErrors.map(x => <div key={x}>{x}</div>)}</div>}
        {pdfRows && pdfRows.length > 0 && (
          <div style={{ fontSize: 12.5, marginTop: 8, background: "#fef3c7", color: "#92400e", borderRadius: 8, padding: "6px 10px" }}>
            <strong>TVA déjà déclarée</strong> : proposée « oui » quand le PDF montre l'acompte réglé à une date antérieure au début de l'exercice ; « non » si la date de règlement est postérieure ou absente. Confirmez chaque ligne ci-dessous (survolez le choix pour voir la date lue). Un acompte qui n'était pas réglé au début de l'exercice est aussi à reprendre en créance d'ouverture (rappel dans la colonne Contrôle). Règles à valider à la révision annuelle des comptes.
          </div>
        )}
        {preview && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontWeight: 700 }}>Aperçu — rien n'est encore créé</div>
            <div style={{ fontSize: 12.5, margin: "4px 0 8px" }}>{preview.valid} acompte(s) valide(s) sur {preview.rows} · repris au 419 au {frDate(preview.opening_date)} : <strong>{eur(preview.total_419)}</strong> · TVA d'acomptes non encore déclarée (collectée à la facture de solde) : {eur(preview.total_vat_not_declared)}</div>
            <div style={{ overflowX: "auto" }}>
              <table className="table" style={{ width: "100%", fontSize: 12 }}>
                <thead><tr><th style={{ textAlign: "left" }}>Réf.</th><th style={{ textAlign: "left" }}>Date</th><th style={{ textAlign: "left" }}>Client</th><th style={{ textAlign: "right" }}>TTC</th><th style={{ textAlign: "right" }}>TVA déclarée</th><th style={{ textAlign: "right" }}>Au 419</th><th style={{ textAlign: "left" }}>Contrôle</th></tr></thead>
                <tbody>{preview.preview.map(r => (
                  <tr key={r.line}><td>{r.ref}</td><td>{frDate(r.date)}</td><td>{r.client_name || "—"}</td><td style={{ textAlign: "right" }}>{eur(r.ttc)}</td>
                    <td style={{ textAlign: "right" }}>{pdfRows ? (
                      <select className="input" style={{ width: "auto", fontSize: 11.5, padding: "1px 4px" }} value={(pdfRows.find(x => (x.ref || "").toUpperCase() === r.ref) || {}).declared || "non"} disabled={busy}
                        onChange={e => setDeclared((pdfRows.find(x => (x.ref || "").toUpperCase() === r.ref) || {}).ref, e.target.value)} title={(pdfRows.find(x => (x.ref || "").toUpperCase() === r.ref) || {}).declared_default}>
                        <option value="oui">oui ({eur(r.vat_declared + r.vat_not_declared)})</option><option value="non">non</option>
                      </select>) : eur(r.vat_declared)}</td><td style={{ textAlign: "right" }}>{eur(r.base_419)}</td>
                    <td style={{ color: r.errors.length ? "#b91c1c" : r.warnings.length ? "#b45309" : "#16a34a" }}>{r.errors.length ? r.errors.join(" ") : r.warnings.length ? r.warnings.join(" ") : "✓"}</td></tr>
                ))}</tbody>
              </table>
            </div>
            <button className="btn primary" style={{ marginTop: 8 }} disabled={busy || preview.valid !== preview.rows || !preview.valid} onClick={create}>Créer {preview.valid} acompte(s) d'ouverture</button>
            {preview.valid !== preview.rows && <div style={{ fontSize: 12, color: "#b45309", marginTop: 4 }}>{pdfRows ? "Les lignes en erreur ne peuvent pas être créées : retirez le PDF concerné (choisissez à nouveau les autres PDF), ou utilisez le modèle Excel." : "Corrigez les lignes en erreur dans le fichier, puis choisissez-le à nouveau."}</div>}
          </div>
        )}
        {result && <div style={{ background: "#dcfce7", color: "#166534", borderRadius: 8, padding: "8px 12px", marginTop: 10, fontSize: 13 }}>✓ {result.created.length} acompte(s) repris au 419 ({eur(result.total_419)}).</div>}
        {ledger.length > 0 && (
          <div style={{ marginTop: 14 }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Acomptes repris</div>
            <table className="table" style={{ width: "100%", fontSize: 12 }}>
              <thead><tr><th style={{ textAlign: "left" }}>Réf.</th><th style={{ textAlign: "left" }}>Origine</th><th style={{ textAlign: "left" }}>Date</th><th style={{ textAlign: "right" }}>Au 419</th><th style={{ textAlign: "right" }}>Déduit</th><th style={{ textAlign: "right" }}>Reste</th></tr></thead>
              <tbody>{ledger.map(d => (
                <tr key={d.id}><td>{d.ref}</td><td>{d.kind === "ouverture" ? "Ouverture" : "Facture d'acompte"}</td><td>{frDate(d.deposit_date)}</td><td style={{ textAlign: "right" }}>{eur(d.base_419)}</td><td style={{ textAlign: "right" }}>{eur(d.deducted_419)}</td><td style={{ textAlign: "right" }}>{eur(d.remaining_419)}</td></tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
